import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { FileBarChart, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';



function ModePill({ mode }) {
  const isAuto = (mode || '').toLowerCase() === 'auto';
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${isAuto ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>{mode || 'manual'}</span>;
}

export default function BillingLogs() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [planFilter, setPlanFilter] = useState('');
  const [plans, setPlans] = useState([]);
  const PAGE_SIZE = 25;

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await api.billingLogsList(token, { fee_plan_id: planFilter || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
      setItems(r.items || r || []);
      setTotal(r.total ?? (r.items || r || []).length);
    } catch (e) {
      if (e?.status === 403) return;
      setItems([]);
    } finally { setLoading(false); }
  }, [token, page, planFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (token) {
      api.feePlansList(token, { limit: 100 }).then(r => setPlans(r.items || r || [])).catch(() => {});
    }
  }, [token]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="billing-logs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 flex items-center gap-3"><FileBarChart size={28} className="text-indigo-600" /> Billing Logs</h1>
          <p className="text-gray-500 text-sm mt-1">{total} billing run{total !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={load} data-testid="billing-logs-refresh"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">
          <RotateCcw size={16} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        {plans.length > 0 && (
          <select data-testid="billing-log-plan-filter" value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
            className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white text-gray-700 font-semibold">
            <option value="">All Plans</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="billing-logs-empty">
          <div className="w-20 h-20 rounded-2xl bg-indigo-50 flex items-center justify-center mb-5"><FileBarChart size={36} className="text-indigo-400" /></div>
          <h3 className="text-gray-900 font-bold text-lg mb-2">No billing runs yet</h3>
          <p className="text-gray-500 text-sm mb-5 max-w-sm">Create a Fee Plan and run billing to see history here.</p>
          <button onClick={() => navigate('/school/fee-plans')} className="px-5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700">Go to Fee Plans</button>
        </div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden" data-testid="billing-logs-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Run Date</th>
                <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Plan</th>
                <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Mode</th>
                <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Period</th>
                <th className="text-center px-4 py-3 text-gray-500 text-xs font-bold uppercase">Billed</th>
                <th className="text-center px-4 py-3 text-gray-500 text-xs font-bold uppercase">Invoices</th>
                <th className="text-center px-4 py-3 text-gray-500 text-xs font-bold uppercase">Skipped</th>
                <th className="text-center px-4 py-3 text-gray-500 text-xs font-bold uppercase">Errors</th>
              </tr></thead>
              <tbody>
                {items.map(log => (
                  <tr key={log.id} data-testid={`billing-log-row-${log.id}`} onClick={() => navigate(`/school/billing-logs/${log.id}`)}
                    className="border-b border-gray-100 last:border-0 hover:bg-indigo-50/40 cursor-pointer transition-colors h-[52px]">
                    <td className="px-5 py-3 text-gray-900 font-semibold">{log.run_date || log.created_at ? new Date(log.run_date || log.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{log.plan_name || log.fee_plan_name || '—'}</td>
                    <td className="px-4 py-3"><ModePill mode={log.mode || log.run_mode} /></td>
                    <td className="px-4 py-3 text-gray-600 tabular-nums">{log.period || '—'}</td>
                    <td className="px-4 py-3 text-center text-gray-700 tabular-nums">{log.students_billed ?? '—'}</td>
                    <td className="px-4 py-3 text-center text-gray-700 tabular-nums">{log.invoices_created ?? '—'}</td>
                    <td className="px-4 py-3 text-center text-gray-500 tabular-nums">{log.skipped_already_billed ?? log.skipped ?? 0}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`tabular-nums ${(log.errors?.length || log.error_count || 0) > 0 ? 'text-red-600 font-bold' : 'text-gray-400'}`}>{log.errors?.length ?? log.error_count ?? 0}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-1">
              <p className="text-gray-500 text-xs">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50"><ChevronLeft size={14} className="inline" /> Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">Next <ChevronRight size={14} className="inline" /></button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
